import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import '../styles/ModelSelector.css';

function ModelSelector({ onModelChange }) {
  const [models, setModels] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [currentModel, setCurrentModel] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    // Fetch available models and configurations when component mounts
    fetchModels();
    fetchConfigurations();
    
    // Listen for model change events from ModelEvaluator
    const handleModelChange = (event) => {
      if (event.detail && event.detail.model) {
        setCurrentModel(event.detail.model);
        // Also refresh the configurations to update the UI
        fetchConfigurations();
      }
    };
    
    window.addEventListener('modelChanged', handleModelChange);
    
    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener('modelChanged', handleModelChange);
    };
  }, []);

  const fetchModels = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.getAvailableModels();
      setModels(data.available_models || []);
      setCurrentModel(data.current_model || '');
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching models:', error);
      // More specific error message
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError(`Server error: ${error.response.status} - ${error.response.data.message || 'Failed to load models'}`);
      } else if (error.request) {
        // The request was made but no response was received
        setError('Network error: Could not connect to the server. Check your connection.');
      } else {
        // Something happened in setting up the request that triggered an Error
        setError(`Failed to load models: ${error.message}`);
      }
      setIsLoading(false);
    }
  };

  const fetchConfigurations = async () => {
    try {
      const response = await apiService.getModelConfigurations();
      setConfigurations(response.configurations || []);
    } catch (error) {
      console.error('Error fetching model configurations:', error);
    }
  };

  // This function now handles selecting a configuration instead of just a model
  const handleConfigSelect = async (configId) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Apply the selected configuration
      const response = await apiService.applyModelConfiguration(configId);
      
      // Update the current model based on the applied configuration
      if (response && response.platform) {
        setCurrentModel(response.platform);
        
        // Refresh the models list to update UI
        await fetchModels();
        
        // Refresh the configurations list
        await fetchConfigurations();
        
        setIsOpen(false);
        
        // Notify parent component of model change
        if (onModelChange) {
          onModelChange(response.platform);
        }
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error applying configuration:', error);
      // More specific error message
      if (error.response) {
        if (error.response.status === 500) {
          setError(`Server error: The configuration might be missing required settings.`);
        } else {
          setError(`Server error: ${error.response.status} - ${error.response.data.message || 'Failed to apply configuration'}`);
        }
      } else if (error.request) {
        setError('Network error: Could not connect to the server. Check your connection.');
      } else {
        setError(`Failed to apply configuration: ${error.message}`);
      }
      setIsLoading(false);
    }
  };

  // Find the current model details
  const getCurrentModelName = () => {
    // Try to find the active configuration first
    const activeConfig = configurations.find(config => config.active);
    if (activeConfig) {
      return `${getPlatformDisplayName(activeConfig.platform)} (${activeConfig.model_name})`;
    }
    
    // Fallback to model information
    const model = models.find(m => m.id === currentModel);
    return model ? `${model.name} (${model.model_name})` : 'Select Model';
  };

  // Helper to get platform display name
  const getPlatformDisplayName = (platform) => {
    switch (platform) {
      case 'openai': return 'OpenAI';
      case 'azure_openai': return 'Azure OpenAI';
      case 'gemini': return 'Google Gemini';
      case 'transformers': return 'Hugging Face';
      default: return platform;
    }
  };

  // Helper to get platform icon
  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'openai': return 'psychology';
      case 'azure_openai': return 'cloud';
      case 'gemini': return 'auto_awesome';
      case 'transformers': return 'memory';
      default: return 'smart_toy';
    }
  };

  return (
    <div className="model-selector-container">
      <div 
        className="model-selector-button"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="material-icons">memory</span>
        <span className="model-name">
          {isLoading ? 'Loading...' : error ? 'Model Error' : getCurrentModelName()}
        </span>
        <span className="material-icons">{isOpen ? 'expand_less' : 'expand_more'}</span>
      </div>
      
      {isOpen && (
        <div className="model-dropdown">
          <div className="model-dropdown-header">
            <span>Select Model Configuration</span>
          </div>
          
          {error && (
            <div className="model-error">
              <div>{error}</div>
              <button 
                className="retry-button"
                onClick={() => {
                  fetchModels();
                  fetchConfigurations();
                }}
              >
                Retry
              </button>
            </div>
          )}
          
          <ul className="model-list">
            {configurations.map(config => (
              <li 
                key={config.id} 
                className={`model-item ${config.active ? 'active' : ''}`}
                onClick={() => !config.active && handleConfigSelect(config.id)}
              >
                <div className="model-item-icon">
                  <span className="material-icons">{getPlatformIcon(config.platform)}</span>
                </div>
                <div className="model-item-content">
                  <div className="model-item-name">{getPlatformDisplayName(config.platform)}</div>
                  <div className="model-item-details">
                    {config.model_name}
                  </div>
                </div>
                {config.active && (
                  <span className="material-icons model-selected-icon">check_circle</span>
                )}
              </li>
            ))}
            {configurations.length === 0 && (
              <li className="model-item">
                <div className="model-item-content">
                  <div className="model-item-name">No configurations available</div>
                  <div className="model-item-details">
                    Add configurations in Model Configuration
                  </div>
                </div>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default ModelSelector;